import "./AppointmentOverlay.scss";

import { useCallback, useEffect, useMemo, useState } from "react";
import { AppointmentCancellationReason } from "appointment-experience-client";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { RootState } from "../../store";
import { showToast } from "../../store/slices/toastSlice";
import {
  createAppointmentApi,
  fetchAppointmentActivities,
} from "../../api-helpers/AppointmentApiHelper";
import {
  elapsedMinutesCapped,
  findAppointmentStatusId,
  loadAppointment,
} from "../../api-helpers/appointmentWorkflow";
import { Workflow } from "../../constants/Lookups";
import {
  finishAppointment,
  setChecklistRowCount,
  setJourneyStepId,
  toggleCollapsed,
  verifyPatient,
  visitActivitiesLoaded,
} from "../../store/slices/appointmentOverlaySlice";
import { AppointmentTask } from "../../constants/AppointmentOverlayTasks";
import {
  CARE_MANAGEMENT_STEP_ID,
  VisitCompletion,
  careManagementStep,
  journeyOverview,
  withVisitCompletions,
} from "../../constants/AppointmentOverlayJourney";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
  toVisitActivity,
} from "../../constants/AppointmentActivity";
import { useCareJourney } from "../../hooks/useCareJourney";
import { useCareNotes } from "../../hooks/useCareNotes";
import { usePatientInfo } from "../../hooks/usePatientInfo";
import AppointmentOverlayCard from "./AppointmentOverlayCard";
import { useOverlayDock } from "./useOverlayDock";

/**
 * The ways out of an appointment from the overlay: record an outcome, cancel it, or
 * back out of one that was opened by mistake and leave it as if it had never started.
 */
export type AppointmentOutcome = "completed" | "no-show" | "exit" | "canceled";

const OUTCOME_STATUS: Record<AppointmentOutcome, string> = {
  completed: Workflow.APPOINTMENT_STATUS_COMPLETED,
  "no-show": Workflow.APPOINTMENT_STATUS_NO_SHOW,
  exit: Workflow.APPOINTMENT_STATUS_PLANNED,
  canceled: Workflow.APPOINTMENT_STATUS_CANCELED,
};

const OUTCOME_ERROR: Record<AppointmentOutcome, string> = {
  completed: "Could not finish the appointment. Please try again.",
  "no-show": "Could not mark the appointment as a no show. Please try again.",
  exit: "Could not leave the appointment. Please try again.",
  canceled: "Could not cancel the appointment. Please try again.",
};

// A hung request must not leave the overlay stuck on "Saving...".
export const OUTCOME_REQUEST_TIMEOUT_MS = 20000;

const OUTCOME_SUCCESS: Record<AppointmentOutcome, string> = {
  completed: "Appointment completed",
  "no-show": "Appointment marked as a no show",
  exit: "Appointment returned to planned",
  canceled: "Appointment canceled",
};

/**
 * Pinned bottom-right widget showing the tasks for the appointment a clinician is
 * currently working (WCP-13240).
 *
 * Mounted once in ProtectedLayout so it survives <Outlet/> swaps; it needs router
 * context for its navigation actions, which is why it lives there rather than above
 * <RouterProvider> in index.tsx.
 *
 * Fields are selected individually rather than as one object: returning a new object
 * from a single selector would re-render this on every unrelated store update.
 */
const AppointmentOverlay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appointmentId = useSelector(
    (state: RootState) => state.appointmentOverlay.appointmentId
  );
  const patientName = useSelector(
    (state: RootState) => state.appointmentOverlay.patientName
  );
  const patientId = useSelector(
    (state: RootState) => state.appointmentOverlay.patientId
  );
  const detailsPath = useSelector(
    (state: RootState) => state.appointmentOverlay.detailsPath
  );
  const collapsed = useSelector(
    (state: RootState) => state.appointmentOverlay.collapsed
  );
  const connectivity = useSelector(
    (state: RootState) => state.appointmentOverlay.connectivity
  );
  const startedAt = useSelector(
    (state: RootState) => state.appointmentOverlay.startedAt
  );
  const scheduledMinutes = useSelector(
    (state: RootState) => state.appointmentOverlay.scheduledMinutes
  );
  const patientVerified = useSelector(
    (state: RootState) => state.appointmentOverlay.patientVerified
  );

  const appointmentApi = useMemo(() => createAppointmentApi(), []);
  // Which outcome the footer is currently asking to confirm, if any.
  const [pendingOutcome, setPendingOutcome] = useState<
    AppointmentOutcome | null
  >(null);
  const [finishing, setFinishing] = useState(false);
  // The outcome that couldn't be saved, offering Try again or Close anyway.
  const [failedOutcome, setFailedOutcome] = useState<AppointmentOutcome | null>(null);
  // Loaded the first time Cancel appointment is chosen; a cancellation needs a reason.
  const [cancellationReasons, setCancellationReasons] = useState<
    AppointmentCancellationReason[] | null
  >(null);
  const [cancelReason, setCancelReason] = useState("");
  const tasks = useSelector(
    (state: RootState) => state.appointmentOverlay.tasks
  );

  const dock = useOverlayDock();

  // The next care journey step, suggested alongside the checklist. Reloaded on
  // every route change so a form submitted elsewhere shows as done on return.
  // Shown on the same terms as the roadmap card: not for RPM Only patients.
  const location = useLocation();
  // Whether the patient is RPM Only, from their details. The
  // profile header's value wins when it's there (it's live with its toggle);
  // otherwise the overlay's own lookup decides, so the journey doesn't depend
  // on the profile having been opened.
  const patientInfo = usePatientInfo(
    appointmentId && patientId ? patientId : undefined
  );
  const headerRpmOnly = useSelector((state: RootState) =>
    patientId ? state.patientRpmOnly.byPatientId[patientId] : undefined
  );
  const rpmOnly = headerRpmOnly ?? patientInfo.rpmOnly;
  const { roadmap, status: journeyStatus } = useCareJourney(
    appointmentId && patientId ? patientId : undefined,
    location.pathname
  );
  // A Care Management note can be chosen as the focus too, after the journey.
  const careNotes = useCareNotes(
    appointmentId && patientId ? patientId : undefined,
    location.pathname
  );
  const visitActivities = useSelector(
    (state: RootState) => state.appointmentOverlay.visitActivities
  );
  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    fetchAppointmentActivities(appointmentId)
      .then((activities) => {
        if (!cancelled) {
          dispatch(visitActivitiesLoaded(activities.map(toVisitActivity)));
        }
      })
      .catch((error) => {
        console.error("Error loading the appointment's activity:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId, dispatch]);

  // Care notes and assessments submitted this visit, by clinical form.
  const visitCompletions = useMemo(() => {
    const byForm = new Map<string, VisitCompletion[]>();
    visitActivities.forEach((activity) => {
      const isForm =
        activity.activityType === AppointmentActivityType.CARE_NOTE ||
        activity.activityType === AppointmentActivityType.ASSESSMENT;
      if (!isForm || !activity.formId) return;
      if (activity.status !== AppointmentActivityAction.COMPLETED) return;
      byForm.set(activity.formId, [
        ...(byForm.get(activity.formId) ?? []),
        { memberFormId: activity.refId, completedAt: activity.completedAt },
      ]);
    });
    return byForm;
  }, [visitActivities]);

  const journey = useMemo(
    () =>
      patientId && rpmOnly === false
        ? withVisitCompletions(
            {
              ...journeyOverview(roadmap, patientId),
              extras: [careManagementStep(patientId, careNotes ?? [], startedAt)],
            },
            visitCompletions,
            patientId
          )
        : null,
    [roadmap, patientId, rpmOnly, careNotes, startedAt, visitCompletions]
  );

  // The chosen focus lives in the store, so it survives a refresh.
  const journeyStepId = useSelector(
    (state: RootState) => state.appointmentOverlay.journeyStepId
  );

  // Placeholders until everything the first view depends on has answered, so
  // nothing pops in or changes after they go: whether the patient is RPM Only
  // (which decides the journey); the roadmap, for a patient who gets the
  // journey; and the care notes, when Care Management is the focus. Later
  // reloads keep what's shown, so this only covers the first load.
  const rpmOnlyKnown = rpmOnly !== undefined || patientInfo.loaded;
  const checklistLoading =
    !rpmOnlyKnown ||
    (rpmOnly === false && roadmap === null && journeyStatus === "loading") ||
    (rpmOnly === false &&
      journeyStepId === CARE_MANAGEMENT_STEP_ID &&
      careNotes === undefined);

  const handleChooseJourneyStep = useCallback(
    (stepId: string | null) => dispatch(setJourneyStepId(stepId)),
    [dispatch]
  );

  // How many rows the checklist last showed, for the loading placeholders.
  const checklistRowCount = useSelector(
    (state: RootState) => state.appointmentOverlay.checklistRowCount
  );
  // Only called when this tab's rows change. Answering a change to the stored count
  // would have tabs that show different rows writing over each other forever.
  const handleChecklistRowCount = useCallback(
    (count: number) => {
      dispatch(setChecklistRowCount(count));
    },
    [dispatch]
  );

  const handleOpenJourneyForm = useCallback(
    (to: string) => navigate(to),
    [navigate]
  );

  const handleVerifyPatient = useCallback(() => {
    dispatch(verifyPatient());
  }, [dispatch]);

  const handleToggleCollapsed = useCallback(() => {
    dispatch(toggleCollapsed());
  }, [dispatch]);

  const handleTaskAction = useCallback(
    (task: AppointmentTask) => {
      if (task.action) {
        navigate(task.action.to);
      }
    },
    [navigate]
  );

  // Completing an appointment cannot be undone from here, so it is confirmed first.
  // The confirmation lives in the overlay's own footer rather than a modal: the widget
  // is a pinned companion, and throwing a backdrop over the whole app to answer a
  // question it asked would be a heavier interruption than the action warrants.
  const handleFinish = useCallback(() => {
    if (patientVerified) setPendingOutcome("completed");
  }, [patientVerified]);

  const handleNoShow = useCallback(() => {
    setPendingOutcome("no-show");
  }, []);

  const handleExit = useCallback(() => {
    setPendingOutcome("exit");
  }, []);

  const handleCancelAppointment = useCallback(() => {
    setPendingOutcome("canceled");
    if (cancellationReasons) return;
    appointmentApi
      .appointmentControllerGetAppointmentCancellationReasons()
      .then((response) => setCancellationReasons(response.data ?? []))
      .catch((error) => {
        console.error("Error loading cancellation reasons:", error);
        setCancellationReasons([]);
      });
  }, [appointmentApi, cancellationReasons]);

  const handleCancelFinish = useCallback(() => {
    setPendingOutcome(null);
    setFailedOutcome(null);
    setCancelReason("");
  }, []);

  const handleRetryOutcome = useCallback(() => {
    setPendingOutcome(failedOutcome);
    setFailedOutcome(null);
  }, [failedOutcome]);

  // Closes the overlay here only; the appointment stays in progress.
  const handleForceClose = useCallback(() => {
    setFailedOutcome(null);
    setPendingOutcome(null);
    dispatch(finishAppointment());
    dispatch(
      showToast({
        type: "warning",
        title: "",
        message:
          "Appointment panel closed. The appointment is still in progress; finish it from its details page.",
        icon: "warning",
      })
    );
  }, [dispatch]);

  // An appointment finished, cancelled or removed elsewhere closes its overlay.
  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    loadAppointment(appointmentId, { timeout: OUTCOME_REQUEST_TIMEOUT_MS })
      .then((appointment) => {
        const stateName = appointment.status?.stateName;
        if (!cancelled && stateName && stateName !== Workflow.APPOINTMENT_STATUS_INPROGRESS) {
          dispatch(finishAppointment());
        }
      })
      .catch((error) => {
        if (!cancelled && error?.response?.status === 404) {
          dispatch(finishAppointment());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId, dispatch]);

  const handleConnectivityError = useCallback(
    (message: string) => {
      dispatch(
        showToast({
          type: "danger",
          title: "",
          message,
          icon: "error_outline",
        })
      );
    },
    [dispatch]
  );

  const handleConfirmFinish = useCallback(async () => {
    if (!appointmentId || !pendingOutcome) {
      return;
    }

    const statusName = OUTCOME_STATUS[pendingOutcome];
    const isCompleted = pendingOutcome === "completed";

    setFinishing(true);
    try {
      const appointment = await loadAppointment(appointmentId, {
        timeout: OUTCOME_REQUEST_TIMEOUT_MS,
      });
      const statusId = findAppointmentStatusId(appointment, statusName);

      if (!statusId) {
        throw new Error(
          `Appointment cannot move to ${statusName} from ${appointment.status?.stateName}`
        );
      }

      await appointmentApi.appointmentControllerUpdateAppointmentById(
        // Only a completed visit has a duration worth recording; a no show never
        // happened, and an appointment backed out of has not happened yet.
        isCompleted
          ? {
              status: statusId,
              duration: elapsedMinutesCapped(
                startedAt,
                appointment.durationMinutes
              ),
            }
          : pendingOutcome === "canceled"
          ? { status: statusId, cancelledReason: cancelReason }
          : { status: statusId },
        appointmentId,
        { timeout: OUTCOME_REQUEST_TIMEOUT_MS }
      );
    } catch (error) {
      console.error(`Error marking the appointment ${statusName}:`, error);
      dispatch(
        showToast({
          type: "danger",
          title: "",
          message: OUTCOME_ERROR[pendingOutcome],
          icon: "error_outline",
        })
      );
      setFinishing(false);
      setPendingOutcome(null);
      setFailedOutcome(pendingOutcome);
      return;
    }

    setFinishing(false);
    setPendingOutcome(null);
    setCancelReason("");

    if (detailsPath) {
      navigate(detailsPath);
    }
    dispatch(finishAppointment());
    dispatch(
      showToast({
        type: "success",
        title: "",
        message: OUTCOME_SUCCESS[pendingOutcome],
        icon: "check_circle",
      })
    );
  }, [
    appointmentApi,
    appointmentId,
    pendingOutcome,
    cancelReason,
    startedAt,
    detailsPath,
    navigate,
    dispatch,
  ]);

  if (!appointmentId) return null;

  return (
    <AppointmentOverlayCard
      patientName={patientName}
      scheduledMinutes={scheduledMinutes}
      patientPath={patientId ? `/patients/${patientId}` : null}
      detailsPath={detailsPath}
      patientInfoPath={patientId ? `/patients/${patientId}/patientinfo` : null}
      patientVerified={patientVerified}
      onVerifyPatient={handleVerifyPatient}
      appointmentId={appointmentId}
      connectivity={connectivity}
      onConnectivityError={handleConnectivityError}
      tasks={tasks}
      journey={journey}
      journeyStepId={journeyStepId}
      onChooseJourneyStep={handleChooseJourneyStep}
      onOpenJourneyForm={handleOpenJourneyForm}
      collapsed={collapsed}
      onToggleCollapsed={handleToggleCollapsed}
      side={dock.side}
      dragging={dock.dragging}
      cardRef={dock.cardRef}
      gripProps={dock.gripProps}
      onTaskAction={handleTaskAction}
      onFinish={handleFinish}
      onNoShow={handleNoShow}
      onExit={handleExit}
      onCancelAppointment={handleCancelAppointment}
      cancellationReasons={cancellationReasons}
      cancelReason={cancelReason}
      onCancelReasonChange={setCancelReason}
      pendingOutcome={pendingOutcome}
      onCancelFinish={handleCancelFinish}
      failedOutcome={failedOutcome}
      onRetryOutcome={handleRetryOutcome}
      onForceClose={handleForceClose}
      onConfirmFinish={handleConfirmFinish}
      finishing={finishing}
      loading={checklistLoading}
      placeholderCount={
        checklistRowCount && checklistRowCount > 0 ? Math.min(checklistRowCount, 6) : 2
      }
      onChecklistRowCount={handleChecklistRowCount}
    />
  );
};

export default AppointmentOverlay;
